import type { CSSProperties, FC, ReactNode, RefObject } from 'react'

import { ArrowUpRight, ChartLine, CircleCheck, Sparkles } from 'lucide-react'
import { Container } from '@/components/generals/container'
import { FlowLines } from '@/components/generals/flow-lines'
import { ButtonLink } from '@/components/generals/button'
import { DeviceFallback } from '@/components/three/device-fallback'
import { ExpensesWidget, IncomeWidget } from '@/components/dashboard/finance-widgets'

const delay = (ms: number): CSSProperties => ({ '--rise-delay': `${ms}ms` } as CSSProperties)

const CHIP = 'flex items-center gap-2 rounded-full border border-burgundy/10 bg-ivory/80 py-1.5 pl-1.5 pr-3.5 text-xs text-burgundy shadow-[0_12px_30px_-18px_rgb(57_26_24/0.5)] backdrop-blur'

const HeroBackdrop: FC = (): ReactNode => {
    return (
        <div aria-hidden='true' className='pointer-events-none absolute inset-0 z-0 overflow-hidden'>
            <div className='absolute inset-0 bg-[radial-gradient(ellipse_55%_45%_at_50%_60%,rgb(250_248_242/0.85),transparent_72%)]' />

            <FlowLines />

            <div className='absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent to-cream' />

            <div className='absolute left-[4%] top-[43%] hidden lg:block'>
                <div className={`${CHIP} animate-float`} style={{ animationDelay: '-1.5s' }}>
                    <span className='flex size-6 items-center justify-center rounded-full bg-sage/25 text-sage-dark'>
                        <CircleCheck className='size-3.5' />
                    </span>
                    Invoice INV-2048 matched
                </div>
            </div>

            <div className='absolute right-[4%] top-[39%] hidden lg:block'>
                <div className={`${CHIP} animate-float-slow`}>
                    <span className='flex size-6 items-center justify-center rounded-full bg-burgundy/[0.07] text-burgundy'>
                        <ChartLine className='size-3.5' />
                    </span>
                    Forecast refreshed at 6:02 AM
                </div>
            </div>

            <div className='absolute left-[13%] top-[59%] hidden xl:block'>
                <div className={`${CHIP} animate-float-slow`} style={{ animationDelay: '-4s' }}>
                    <span className='flex size-6 items-center justify-center rounded-full bg-positive/10 text-positive'>
                        <ArrowUpRight className='size-3.5' />
                    </span>
                    Income up 8.2% this month
                </div>
            </div>

            <div className='absolute right-[11%] top-[56%] hidden xl:block'>
                <div className={`${CHIP} animate-float`} style={{ animationDelay: '-3s' }}>
                    <span className='flex size-6 items-center justify-center rounded-full bg-burgundy text-gold'>
                        <Sparkles className='size-3.5' />
                    </span>
                    Software spend up 18%
                </div>
            </div>

            <span className='absolute left-[22%] top-[34%] hidden size-1.5 rounded-full bg-gold-dark lg:block'>
                <span className='absolute inset-0 animate-ping rounded-full bg-gold-dark/60 motion-reduce:animate-none' />
            </span>

            <span className='absolute right-[24%] top-[68%] hidden size-1.5 rounded-full bg-burgundy/60 lg:block'>
                <span className='absolute inset-0 animate-ping rounded-full bg-burgundy/40 motion-reduce:animate-none' style={{ animationDelay: '0.8s' }} />
            </span>
        </div>
    )
}

interface HeroProps {
    sectionRef: RefObject<HTMLElement | null>
    slotRef: RefObject<HTMLDivElement | null>
    fallback: boolean
}

export const Hero: FC<HeroProps> = ({ sectionRef, slotRef, fallback }): ReactNode => {
    return (
        <section
            ref={sectionRef}
            id='hero'
            aria-labelledby='hero-title'
            className='relative -mt-[100svh] flex min-h-[100svh] flex-col lg:h-[100svh] lg:min-h-[760px]'
        >
            <HeroBackdrop />

            <Container className='relative z-20 flex flex-1 flex-col pb-6 pt-24 md:pt-28'>
                <div className='flex flex-col items-center text-center'>
                    <span
                        className='animate-rise inline-flex items-center gap-2.5 rounded-full border border-burgundy/15 bg-ivory/60 py-1.5 pl-1.5 pr-3.5 font-mono text-[10.5px] font-medium uppercase tracking-[0.2em] text-stone-dark backdrop-blur'
                        style={delay(60)}
                    >
                        <span aria-hidden='true' className='flex size-[18px] items-center justify-center rounded-[4px] bg-[#F26522] font-sans text-[10px] font-bold not-italic tracking-normal text-white'>
                            Y
                        </span>
                        Backed by Y Combinator
                    </span>

                    <h1 id='hero-title' className='mt-6 font-heading text-display-2xl font-medium text-burgundy'>
                        <span className='-mb-[0.16em] block overflow-hidden pb-[0.16em]'>
                            <span className='animate-line-up block' style={delay(140)}>
                                Smarter <span className='text-stone'>Financial</span>
                            </span>
                        </span>

                        <span className='-mb-[0.16em] block overflow-hidden pb-[0.16em]'>
                            <span className='animate-line-up block' style={delay(240)}>
                                Management
                            </span>
                        </span>
                    </h1>

                    <div className='animate-rise mt-8 flex flex-wrap items-center justify-center gap-3' style={delay(420)}>
                        <ButtonLink href='/signup' size='lg' arrow>
                            Get started
                        </ButtonLink>

                        <ButtonLink href='/demo' variant='secondary' size='lg' className='bg-ivory/40 backdrop-blur'>
                            Request demo
                        </ButtonLink>
                    </div>
                </div>

                <div
                    ref={slotRef}
                    aria-hidden='true'
                    className='relative mx-auto my-6 min-h-[250px] w-full max-w-3xl flex-1 sm:min-h-[300px] lg:my-0 lg:-mb-14 lg:min-h-[200px]'
                >
                    {fallback && (
                        <div className='absolute inset-0 flex items-center justify-center p-6'>
                            <DeviceFallback className='max-w-[440px]' />
                        </div>
                    )}
                </div>

                <div className='relative grid items-end gap-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-10'>
                    <div className='animate-rise max-w-sm' style={delay(700)}>
                        <p className='flex items-center gap-3 font-mono text-[10.5px] font-medium uppercase tracking-[0.2em] text-stone-dark'>
                            <span aria-hidden='true' className='h-px w-8 bg-burgundy/30' />
                            Meet Veyra
                        </p>

                        <p className='mt-3 text-[15px] leading-relaxed text-stone-dark'>
                            Veyra helps finance managers automate manual work, gain real-time visibility into financial performance, and make faster, data-driven decisions through intelligent AI automation.
                        </p>
                    </div>

                    <div className='animate-rise grid gap-3 sm:grid-cols-2 lg:w-[540px]' style={delay(820)}>
                        <ExpensesWidget animation='autoplay' delay={1100} />
                        <IncomeWidget animation='autoplay' delay={1250} />
                    </div>
                </div>
            </Container>
        </section>
    )
}
